import { isArray, isObject } from 'lodash-es';

import type {
  AssetsAssetSchemaTypedWithId,
  AssetsSegmentSchemaWithId,
} from '@iconik-io/iconik-api/src/services/assets';
import type { SegmentEntityBase } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';

import { selectAssetAsSearchItemEntity } from '../../components/asset_title_bar/selectors';
import {
  SEGMENT_FORM_TYPE_SUBCLIP,
  SEGMENT_TYPE_COMMENT,
  SEGMENT_TYPE_GENERIC,
  SEGMENT_TYPE_TAG,
} from '../../components/segment_form/constants';
import type {
  SegmentFormType,
  SegmentType,
} from '../../components/segment_form/types';
import { getCurrentRouteParams } from '../../router/current_route_params';
import type { AppDispatch, GetState } from '../../store/types';
import { getSearchItemMediaType } from '../../utils/media_type';
import { segmentTrackEvent } from '../analytics_tracking/thunks';
import { createSubclipAsset } from '../asset/thunks';
import { createComment } from '../asset_comments/thunks';
import { createMetadataSegment } from '../asset_time_based_metadata/thunks';
import type { SubclipData } from '../asset_time_based_metadata/types';
import { highlightSubclip } from '../panels/subclips/thunks';
import { selectIsSharePage } from '../share/selectors';
import { highlightSegments } from './slice';

export const createSegmentFromFormData =
  (
    formData: SegmentEntityBase,
    options: {
      asset: AssetsAssetSchemaTypedWithId;
      start: number | undefined;
      end: number | undefined;
      segmentType: SegmentType;
      segmentFormType: SegmentFormType | undefined;
      shareLinkUserEmail: string | undefined;
      subclipData: SubclipData | undefined;
      assetVersion?: string;
    },
  ) =>
  async (
    dispatch: AppDispatch,
    getState: GetState,
  ): Promise<AssetsSegmentSchemaWithId | undefined> => {
    const {
      asset,
      start,
      end,
      segmentFormType,
      shareLinkUserEmail,
      segmentType,
      subclipData,
      assetVersion,
    } = options;

    const hasTimecodes = start !== undefined && end !== undefined;

    const state = getState();

    const data: SegmentEntityBase = structuredClone(formData);
    data.segment_type = segmentType;
    data.time_start_milliseconds = start;
    data.time_end_milliseconds = end;
    if (data.metadata) {
      for (const [key, value] of Object.entries(data.metadata)) {
        if (
          !value ||
          (isArray(value) && !value.length) ||
          (isObject(value) && !Object.keys(value).length)
        ) {
          delete data.metadata[key];
        }
      }
    }
    const drawingPrimitives = formData.drawing?.primitives ?? [];
    if (drawingPrimitives.length > 0) {
      data.drawing = formData.drawing;
    }

    if (assetVersion) {
      data.version_id = assetVersion;
    }

    const isSharePage = selectIsSharePage(state);
    const mediaType = getSearchItemMediaType(
      selectAssetAsSearchItemEntity(
        state,
        asset,
        isSharePage,
        assetVersion ?? getCurrentRouteParams().versionId,
      ),
    );

    // Comment segment
    if (data.segment_type === SEGMENT_TYPE_COMMENT) {
      const commentSegment = await dispatch(
        createComment(asset.id, data, shareLinkUserEmail),
      );
      dispatch(
        segmentTrackEvent({
          eventName: 'Segment Created',
          properties: {
            'Media Type': mediaType,
            'Segment Type': 'Comment',
            'Segment Color': data.segment_color,
            'Annotation Tool Used': Boolean(data.drawing),
            'Has Timecodes': hasTimecodes,
            'Is Reply': false,
          },
        }),
      );
      return commentSegment;
    }

    // Time-based metadata segment
    if (
      data.segment_type === SEGMENT_TYPE_GENERIC &&
      segmentFormType !== SEGMENT_FORM_TYPE_SUBCLIP &&
      data.metadata &&
      data.metadata_view_id
    ) {
      if (Object.keys(data.metadata).length === 1 && data.metadata._gcvi_tags) {
        // This is a hack to change the segment type if it uses the tags metadata view
        data.segment_type = SEGMENT_TYPE_TAG;
      }
      const timeBasedMetadataSegment = await dispatch(
        createMetadataSegment(asset.id, data, data.metadata_view_id),
      );
      dispatch(
        segmentTrackEvent({
          eventName: 'Segment Created',
          properties: {
            'Media Type': mediaType,
            'Segment Type': 'Timebased Metadata',
            'Segment Color': data.segment_color,
            'Has Timecodes': hasTimecodes,
          },
        }),
      );
      return timeBasedMetadataSegment;
    }

    // Subclip segment
    const { title, collectionId } = subclipData ?? {};
    if (
      data.segment_type === SEGMENT_TYPE_GENERIC &&
      segmentFormType === SEGMENT_FORM_TYPE_SUBCLIP &&
      data.metadata_view_id &&
      title
    ) {
      const subclipSegment = await dispatch(
        createMetadataSegment(asset.id, data, data.metadata_view_id),
      );
      dispatch(
        segmentTrackEvent({
          eventName: 'Segment Created',
          properties: {
            'Media Type': mediaType,
            'Segment Type': 'Timebased Metadata',
            'Segment Color': data.segment_color,
            'Has Timecodes': hasTimecodes,
          },
        }),
      );
      const subclip = await dispatch(
        createSubclipAsset(title, subclipSegment, collectionId, 'Segment Form'),
      );
      if (subclip) {
        dispatch(highlightSubclip([subclip.id]));
        return subclipSegment;
      }
    }
  };

let highlightTimer: number | undefined;

export const highlightSegmentsTemporarily =
  (segmentIds: string[], timeout = 2000) =>
  (dispatch: AppDispatch) => {
    if (!segmentIds.length) {
      dispatch(highlightSegments([]));
      return;
    }
    dispatch(highlightSegments(segmentIds));
    if (highlightTimer) {
      clearTimeout(highlightTimer);
    }
    highlightTimer = window.setTimeout(() => {
      highlightTimer = undefined;
      dispatch(highlightSegments([]));
    }, timeout);
  };
